import { useEffect, useRef, useState } from 'react';
import { hasFinePointer, prefersReducedMotion } from '../lib/motion';
import { ArrowUpRightIcon, ExternalIcon, PlayIcon } from './Icons';

/** Time constant of the trailing ease; ~0.5s to settle, like an expo-out tween. */
const FOLLOW_MS = 80;
/** The plate morphs a touch slower than the dot trails, so the reshape reads. */
const MORPH_MS = 90;
/** How far the dot stretches along its direction of travel. */
const SKEW_PER_PX = 0.0035;
const SKEW_MAX = 0.22;
/** A covering plate still leans this share toward the real pointer, so it feels magnetic, not frozen. */
const PLATE_PULL = 0.12;
/** The plate overshoots filled controls by this much and never leans further, so no edge peeks out. */
const PLATE_BLEED = 3;
/** Idle plate matches the rendered dot (100px circle at scale 0.1). */
const DOT_SIZE = 10;
/** Bare text links get breathing room; controls with their own shape are covered exactly. */
const TEXT_PAD_X = 10;
const TEXT_PAD_Y = 6;
const TEXT_RADIUS = 10;
/** Past this the plate would flood a region rather than cover a control. */
const PLATE_MAX_W = 560;
const PLATE_MAX_H = 160;
/** How often a covering plate re-checks that its control is still what the pointer is over. */
const HIT_CHECK_MS = 120;
/** Rows inside a menu get an inset highlight, native-menu style, and the plate is clipped to the menu. */
const CONTAINER = '[role="menu"], [role="listbox"], [data-cursor-contain]';
const ROW_INSET_X = 6;
const ROW_INSET_Y = 2;
const ROW_RADIUS = 8;
/** The action pill keeps this clearance from the viewport edges. */
const PILL_EDGE = 8;
const PILL_HEIGHT = 44;
/** Outlasts the plate's 0.2s CSS fade, so it is fully invisible before snapping back to dot size. */
const MORPH_FADE_MS = 260;
/** A menu dipping below this while fading is closing; the plate lets go of it at once. */
const HOST_CLOSING = 0.98;
/** A released plate follows its menu's opacity out; once the menu is this faded the dot takes over. */
const HOST_GONE = 0.02;
/** Safety net if the menu never finishes fading (it should unmount at its own exit time). */
const RELEASE_MS = 400;

type CursorState = 'default' | 'pointer' | 'plate' | 'text' | 'hidden';

const INTERACTIVE =
  'a, button, [role="button"], [role="menuitem"], [role="menuitemradio"], [role="option"], label, summary, select, [data-cursor-stick]';
const HIDDEN = 'input, textarea, iframe, video, [contenteditable="true"], [data-cursor="hidden"]';
const NO_PLATE = '[data-cursor="dot"], :disabled, [aria-disabled="true"]';
/** Channel spread above which a fill counts as a brand colour rather than a neutral. */
const CHROMA_MIN = 60;

type Box = { x: number; y: number; w: number; h: number; r: number };

function channels(color: string) {
  const [r = 0, g = 0, b = 0, a = 1] = (color.match(/[\d.]+/g) ?? []).map(Number);
  return { r, g, b, a };
}

function isBare(style: CSSStyleDeclaration) {
  return (
    channels(style.backgroundColor).a === 0 &&
    parseFloat(style.borderTopLeftRadius) === 0 &&
    style.borderTopWidth === '0px'
  );
}

/** Inverting a brand fill lands on its complement (navy turns gold); those get brightened instead. */
function isChromatic(style: CSSStyleDeclaration) {
  const { r, g, b, a } = channels(style.backgroundColor);
  return a > 0.5 && Math.max(r, g, b) - Math.min(r, g, b) > CHROMA_MIN;
}

function inheritedOpacity(el: HTMLElement) {
  if (!el.isConnected) return 0;
  let opacity = 1;
  for (let node: HTMLElement | null = el; node && node !== document.body; node = node.parentElement) {
    opacity *= parseFloat(getComputedStyle(node).opacity) || 0;
  }
  return opacity;
}

/**
 * Trailing cursor in the manner of Cuberto's mouse-follower: an inverting dot
 * that eases after the pointer and stretches with speed. Over a button or link
 * it swells into a plate that takes that control's exact box and radius, so the
 * inverting blend repaints the whole control. Over media ([data-cursor-text])
 * it becomes an action pill: an icon chip ([data-cursor-icon], tinted by
 * [data-cursor-accent]) beside the label. The native cursor stays visible.
 */
export function CursorFollower() {
  const [enabled] = useState(() => hasFinePointer() && !prefersReducedMotion());
  const rootRef = useRef<HTMLDivElement>(null);
  const labelRef = useRef<HTMLSpanElement>(null);
  const plateRef = useRef<HTMLDivElement>(null);
  const pillRef = useRef<HTMLSpanElement>(null);
  const pillHostRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const root = rootRef.current;
    const label = labelRef.current;
    const plate = plateRef.current;
    const pill = pillRef.current;
    const pillHost = pillHostRef.current;
    if (!enabled || !root || !label || !plate || !pill || !pillHost) return;

    const target = { x: -200, y: -200 };
    const pos = { ...target };
    let pointer = { ...target };
    const box: Box = { x: -200, y: -200, w: DOT_SIZE, h: DOT_SIZE, r: DOT_SIZE / 2 };
    let covered: HTMLElement | null = null;
    let container: HTMLElement | null = null;
    /** Keeps clipping a collapsing plate to the menu it just left, so it exits through the edge. */
    let clipHost: HTMLElement | null = null;
    /** The menu row whose text is currently forced black; only set once the plate actually covers it. */
    let marked: HTMLElement | null = null;
    /** Whatever the pointer currently resolves to, so scroll re-checks can skip unchanged targets. */
    let current: Element | null = null;
    let lastCenter: { x: number; y: number } | null = null;
    let bare = false;
    let pillWidth = 0;
    let pillShift = 0;
    let fadeStart = 0;
    let coverRadius = 0;
    let lastHitCheck = 0;
    let frame = 0;
    let last = 0;
    let lastDim = 1;
    /** A menu released mid-exit; once it unmounts, whatever it uncovered under a still pointer is re-read. */
    let leaving: HTMLElement | null = null;
    /** The row of a closing menu that the plate keeps riding while it fades out. */
    let released: HTMLElement | null = null;
    let releaseStart = 0;

    // The plate stays lit while it shrinks back and hands off to the dot only once they
    // match; overlapping exclusion layers would punch a hole where they cross.
    const setPlateOn = (on: boolean, { regrow = false } = {}) => {
      const value = on ? 'true' : 'false';
      if (plate.dataset.on !== value) plate.dataset.on = value;
      if (root.dataset.plate !== value) root.dataset.plate = value;
      if (!on) {
        clipHost = null;
        released = null;
        delete plate.dataset.fade;
        delete plate.dataset.enter;
        delete plate.dataset.release;
      }
      // A plate that faded out with its menu hands off to nothing visible; the dot grows back in
      // rather than popping up at full size a beat later.
      if (!on && regrow) {
        root.dataset.regrow = '';
        void getComputedStyle(root, '::before').transform;
        delete root.dataset.regrow;
      }
    };

    const clearMarked = () => {
      marked?.removeAttribute('data-cursor-covered');
      marked = null;
    };

    const pillBox = (): Box => ({
      x: pos.x + pillShift,
      y: pos.y,
      w: pillWidth || DOT_SIZE,
      h: PILL_HEIGHT,
      r: PILL_HEIGHT / 2,
    });

    const setState = (state: CursorState) => {
      const from = root.dataset.state;
      if (from !== state) root.dataset.state = state;
      if (state === 'plate') {
        if (released) {
          released = null;
          delete plate.dataset.release;
          clearMarked();
          Object.assign(box, { x: pointer.x, y: pointer.y, w: DOT_SIZE, h: DOT_SIZE, r: DOT_SIZE / 2 });
        }
        // Coming off an action pill: the cover starts as the pill's shape and fades in while it
        // stretches onto the control, so the two read as one object changing form.
        if (from === 'text' && plate.dataset.on !== 'true') {
          Object.assign(box, pillBox());
          plate.dataset.enter = '';
        } else if (from !== 'text') {
          delete plate.dataset.enter;
        }
        setPlateOn(true);
        delete plate.dataset.fade;
      } else if ((state === 'text' || state === 'hidden') && plate.dataset.on === 'true') {
        // The plate stretches into the pill (or folds away) while fading, instead of collapsing to a
        // dot first and then letting the pill pop in separately.
        plate.dataset.fade = '';
        fadeStart = performance.now();
        if (state === 'text') root.dataset.morph = '';
      }
      if (state !== 'text') delete root.dataset.morph;
    };

    const plateTarget = (): Box => {
      const row = covered ?? released;
      if (!row || !row.isConnected) {
        lastCenter = null;
        if (root.dataset.state === 'text' && plate.dataset.on === 'true') return pillBox();
        // A released row whose menu has unmounted: hold the last box so the plate fades out where it
        // is instead of snapping to the viewport corner before the release timer ends.
        if (released && !row?.isConnected) return { ...box };
        return { x: pos.x, y: pos.y, w: DOT_SIZE, h: DOT_SIZE, r: DOT_SIZE / 2 };
      }
      const rect = row.getBoundingClientRect();
      const cx = rect.left + rect.width / 2;
      const cy = rect.top + rect.height / 2;
      // Ride along when the control itself moves (scroll, sticky bars), instead of easing after it.
      if (lastCenter) {
        box.x += cx - lastCenter.x;
        box.y += cy - lastCenter.y;
      }
      lastCenter = { x: cx, y: cy };
      if (container || released) {
        const w = rect.width - ROW_INSET_X * 2;
        const h = rect.height - ROW_INSET_Y * 2;
        return { x: cx, y: cy, w, h, r: Math.min(ROW_RADIUS, h / 2) };
      }
      const padX = bare ? TEXT_PAD_X : PLATE_BLEED;
      const padY = bare ? TEXT_PAD_Y : PLATE_BLEED;
      const w = rect.width + padX * 2;
      const h = rect.height + padY * 2;
      const radius = bare ? TEXT_RADIUS : coverRadius + PLATE_BLEED;
      const lean = (d: number) => Math.max(-PLATE_BLEED, Math.min(PLATE_BLEED, d * PLATE_PULL));
      return {
        x: cx + lean(pointer.x - cx),
        y: cy + lean(pointer.y - cy),
        w,
        h,
        r: Math.min(radius, w / 2, h / 2),
      };
    };

    const setStretch = (angle: number, skew: number) => {
      root.style.setProperty('--dot-rotate', `${angle}deg`);
      root.style.setProperty('--dot-scale', `${1 + skew} ${1 - skew}`);
    };

    const tick = (now: number) => {
      const dt = Math.min(64, now - (last || now - 16));
      last = now;

      const k = 1 - Math.exp(-dt / FOLLOW_MS);
      const vx = (target.x - pos.x) * k;
      const vy = (target.y - pos.y) * k;
      pos.x += vx;
      pos.y += vy;
      root.style.transform = `translate3d(${pos.x}px, ${pos.y}px, 0)`;
      pillHost.style.transform = root.style.transform;
      if (root.dataset.state === 'text') {
        setStretch(0, 0);
      } else {
        const speed = Math.hypot(vx, vy) * (16 / Math.max(dt, 1));
        setStretch((Math.atan2(vy, vx) * 180) / Math.PI, Math.min(speed * SKEW_PER_PX, SKEW_MAX));
      }

      // Sheets, menus and route changes can slide over a control without any mouseover firing.
      if (covered && (!covered.isConnected || now - lastHitCheck > HIT_CHECK_MS)) {
        lastHitCheck = now;
        const hit = document.elementFromPoint(pointer.x, pointer.y);
        if (!hit || !covered.contains(hit)) evaluate(hit);
      }
      if (plate.dataset.on !== 'true') Object.assign(box, { x: pos.x, y: pos.y, w: DOT_SIZE, h: DOT_SIZE, r: DOT_SIZE / 2 });
      const goal = plateTarget();
      const m = 1 - Math.exp(-dt / MORPH_MS);
      box.x += (goal.x - box.x) * m;
      box.y += (goal.y - box.y) * m;
      box.w += (goal.w - box.w) * m;
      box.h += (goal.h - box.h) * m;
      box.r += (goal.r - box.r) * m;
      plate.style.width = `${box.w}px`;
      plate.style.height = `${box.h}px`;
      plate.style.borderRadius = `${box.r}px`;
      // `translate`, not `transform`: the press `scale` is applied after `transform` and would shrink the
      // offset toward the viewport corner, throwing the plate left on every click.
      plate.style.translate = `${box.x - box.w / 2}px ${box.y - box.h / 2}px`;
      // The plate inverts what is under it, so dimming it in step with a fading menu lets the page
      // bleed through and the row flashes light mid-fade. Once the menu starts closing the plate
      // stops inverting (see data-release) and its opacity follows the menu's, frame by frame.
      const dim = plate.dataset.on === 'true' && clipHost ? inheritedOpacity(clipHost) : 1;
      const opacity = dim < 1 ? `${dim}` : '';
      if (plate.style.opacity !== opacity) plate.style.opacity = opacity;
      const hostClosing = Boolean(covered && container) && dim < HOST_CLOSING && dim < lastDim;
      lastDim = dim;
      if (hostClosing) {
        const row = covered as HTMLElement;
        leaving = clipHost;
        released = row;
        releaseStart = now;
        covered = null;
        container = null;
        current = null;
        // Solid, non-inverting from here on; the row's text stays light on it until the menu is gone.
        if (marked !== row) clearMarked();
        row.setAttribute('data-cursor-covered', 'released');
        marked = row;
        plate.dataset.release = '';
      } else if (released && (dim < HOST_GONE || now - releaseStart > RELEASE_MS)) {
        clearMarked();
        setPlateOn(false, { regrow: true });
        plate.style.opacity = '';
        plate.style.clipPath = '';
        lastCenter = null;
        Object.assign(box, { x: pos.x, y: pos.y, w: DOT_SIZE, h: DOT_SIZE, r: DOT_SIZE / 2 });
      } else if (leaving && !leaving.isConnected && !released) {
        leaving = null;
        evaluate(document.elementFromPoint(pointer.x, pointer.y));
      }
      if (clipHost) {
        const c = clipHost.getBoundingClientRect();
        const left = box.x - box.w / 2;
        const top = box.y - box.h / 2;
        const clip = [
          Math.max(0, c.top - top),
          Math.max(0, left + box.w - c.right),
          Math.max(0, top + box.h - c.bottom),
          Math.max(0, c.left - left),
        ];
        plate.style.clipPath = `inset(${clip.map((v) => `${v}px`).join(' ')})`;
      } else if (plate.style.clipPath) {
        plate.style.clipPath = '';
      }
      if (root.dataset.state === 'text' && pillWidth) {
        const half = pillWidth / 2 + PILL_EDGE;
        const overLeft = Math.max(0, half - pos.x);
        const overRight = Math.max(0, pos.x + half - window.innerWidth);
        pillShift = overLeft - overRight;
        pillHost.style.setProperty('--pill-shift', `${pillShift}px`);
      }
      if (!covered && 'fade' in plate.dataset && now - fadeStart > MORPH_FADE_MS) setPlateOn(false);
      // The row's text turns black only once the plate has arrived over it, so it never flashes black
      // on the bare menu while the plate is still on its way.
      const overRow =
        Boolean(container && covered) &&
        box.w > goal.w * 0.6 &&
        Math.hypot(box.x - goal.x, box.y - goal.y) < Math.max(box.w, box.h) / 2;
      if (overRow && covered) {
        if (marked !== covered) {
          marked?.removeAttribute('data-cursor-covered');
          covered.setAttribute('data-cursor-covered', '');
          marked = covered;
        }
      } else if (marked && !released) {
        clearMarked();
      }
      // Hand off to the dot once the plate has shrunk to its size, so the swap has no visible step.
      if (!covered && !released && box.w < DOT_SIZE * 1.2 && box.h < DOT_SIZE * 1.2) setPlateOn(false);

      const dotSettled = Math.abs(target.x - pos.x) < 0.1 && Math.abs(target.y - pos.y) < 0.1;
      const plateSettled =
        Math.abs(goal.x - box.x) < 0.1 &&
        Math.abs(goal.y - box.y) < 0.1 &&
        Math.abs(goal.w - box.w) < 0.1 &&
        Math.abs(goal.h - box.h) < 0.1;
      // While covering, keep measuring: the control can move under a still pointer (sticky bars, menus).
      if (dotSettled && plateSettled && !covered && !leaving && !released) {
        root.style.transform = `translate3d(${target.x}px, ${target.y}px, 0)`;
        pillHost.style.transform = root.style.transform;
        setStretch(0, 0);
        frame = 0;
        last = 0;
        return;
      }
      frame = requestAnimationFrame(tick);
    };

    const wake = () => {
      if (!frame) frame = requestAnimationFrame(tick);
    };

    const onMove = (e: PointerEvent) => {
      if (e.pointerType !== 'mouse') return;
      // Appear under the pointer on (re-)entry rather than gliding in from where it left.
      if (root.dataset.visible !== 'true') {
        pos.x = e.clientX;
        pos.y = e.clientY;
        Object.assign(box, { x: e.clientX, y: e.clientY, w: DOT_SIZE, h: DOT_SIZE, r: DOT_SIZE / 2 });
        root.dataset.visible = 'true';
        plate.dataset.visible = 'true';
      }
      pointer = { x: e.clientX, y: e.clientY };
      target.x = pointer.x;
      target.y = pointer.y;
      wake();
    };

    const evaluate = (el: Element | null) => {
      const previous = covered;
      covered = null;
      container = null;
      const control = el?.closest<HTMLElement>(INTERACTIVE) ?? null;
      const media = el?.closest<HTMLElement>('[data-cursor-text]') ?? null;
      current = media ?? control;
      if (!el || el.closest(HIDDEN)) {
        setState('hidden');
      } else if (media) {
        label.textContent = media.dataset.cursorText ?? '';
        root.dataset.icon = media.dataset.cursorIcon ?? 'view';
        if (media.dataset.cursorAccent) pillHost.style.setProperty('--cursor-accent', media.dataset.cursorAccent);
        else pillHost.style.removeProperty('--cursor-accent');
        pillWidth = pill.offsetWidth;
        const half = pillWidth / 2 + PILL_EDGE;
        pillShift = Math.max(0, half - pos.x) - Math.max(0, pos.x + half - window.innerWidth);
        setState('text');
      } else if (control) {
        const rect = control.getBoundingClientRect();
        if (leaving?.contains(control) && inheritedOpacity(leaving) < HOST_CLOSING) {
          setState('default');
        } else if (control.matches(NO_PLATE) || rect.width > PLATE_MAX_W || rect.height > PLATE_MAX_H) {
          setState('pointer');
        } else {
          const style = getComputedStyle(control);
          covered = control;
          container = control.closest<HTMLElement>(CONTAINER);
          // Crossing a menu edge, the clip would chop the plate mid-glide: a white cover sheared off
          // the trigger and a black sliver sliding in along the menu rim. Regrow from the pointer instead.
          if (plate.dataset.on === 'true' && container !== clipHost) {
            Object.assign(box, { x: pointer.x, y: pointer.y, w: DOT_SIZE, h: DOT_SIZE, r: DOT_SIZE / 2 });
          }
          if (covered !== previous) lastCenter = null;
          clipHost = container;
          bare = isBare(style);
          coverRadius = parseFloat(style.borderTopLeftRadius) || 0;
          plate.dataset.tone = !container && isChromatic(style) ? 'chroma' : 'invert';
          setState('plate');
        }
      } else {
        setState('default');
      }
      wake();
    };

    const onOver = (e: MouseEvent) => evaluate(e.target instanceof Element ? e.target : null);
    const onDown = () => {
      root.setAttribute('data-active', '');
      plate.setAttribute('data-active', '');
    };
    const onUp = () => {
      root.removeAttribute('data-active');
      plate.removeAttribute('data-active');
    };
    const onLeave = () => {
      root.dataset.visible = 'false';
      plate.dataset.visible = 'false';
    };

    // Content slides under a still pointer while scrolling, and no mouseover fires for that.
    let scrollFrame = 0;
    const onScroll = () => {
      if (scrollFrame || root.dataset.visible !== 'true') return;
      scrollFrame = requestAnimationFrame(() => {
        scrollFrame = 0;
        const hit = document.elementFromPoint(pointer.x, pointer.y);
        if (hit && current?.contains(hit)) return;
        evaluate(hit);
      });
    };

    const doc = document.documentElement;
    doc.setAttribute('data-cursor-plate', '');
    window.addEventListener('pointermove', onMove, { passive: true });
    doc.addEventListener('mouseover', onOver, { passive: true });
    doc.addEventListener('mousedown', onDown, { passive: true });
    doc.addEventListener('mouseup', onUp, { passive: true });
    doc.addEventListener('mouseleave', onLeave);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      cancelAnimationFrame(frame);
      cancelAnimationFrame(scrollFrame);
      marked?.removeAttribute('data-cursor-covered');
      doc.removeAttribute('data-cursor-plate');
      window.removeEventListener('pointermove', onMove);
      doc.removeEventListener('mouseover', onOver);
      doc.removeEventListener('mousedown', onDown);
      doc.removeEventListener('mouseup', onUp);
      doc.removeEventListener('mouseleave', onLeave);
      window.removeEventListener('scroll', onScroll);
    };
  }, [enabled]);

  if (!enabled) return null;
  return (
    <>
      <div ref={plateRef} className="cursor-plate" data-on="false" data-visible="true" aria-hidden="true" />
      <div ref={rootRef} className="cursor" data-state="default" data-visible="false" data-icon="view" aria-hidden="true" />
      {/* Its own layer, outside the dot's inverting blend, so the dot can grow back underneath the
          pill while it folds away instead of waiting for it to vanish. */}
      <div ref={pillHostRef} className="cursor-pill-host" aria-hidden="true">
        <span ref={pillRef} className="cursor-pill">
          <span className="cursor-chip">
            <PlayIcon data-icon="play" />
            <ArrowUpRightIcon data-icon="view" />
            <ExternalIcon data-icon="external" />
          </span>
          <span ref={labelRef} className="cursor-label" />
        </span>
      </div>
    </>
  );
}
